import { useKeyboard } from "client/hooks/utils/useKeyboard";
import { useOnOutsideClick } from "client/hooks/utils/useOnOutsideClick";
import { type ReactNode, useRef, type ReactElement } from "react";
import { List } from "../List/List";
import { stopPropagation } from "client/utils/stopPropagation";

export interface DropDownListProps<T extends string> {
    items: Partial<Record<T, ReactNode>>;
    callback?: (key: T) => void;
    children: ReactElement;
    isOpen: boolean;
    close: () => void;
    open: () => void;
    testId?: string;
}

export const DropDownList = <T extends string>({
    items,
    callback,
    children,
    isOpen,
    close,
    open,
    testId = "dropdown-list",
}: DropDownListProps<T>) => {
    const parentRef = useRef<HTMLDivElement | null>(null);
    useOnOutsideClick(parentRef, isOpen, close);
    useKeyboard("Escape", isOpen, close);

    const handleParentClick = () => (isOpen ? close() : open());
    return (
        <div
            onClick={handleParentClick}
            ref={parentRef}
            className="relative"
            style={{
                zIndex: isOpen ? 999 : 0,
            }}
        >
            {children}
            {isOpen && (
                <div
                    data-testid={testId}
                    onClick={stopPropagation()}
                    className="absolute left-0 z-10 overflow-hidden translate-y-2 bg-white border border-gray-200 rounded-lg shadow-lg top-full dark:bg-darkSurface-200 dark:border-darkSurface-100"
                >
                    <List items={items} callback={callback} />
                </div>
            )}
        </div>
    );
};
